import { Utils } from "./utils";
import type { Payload } from "./config";
import type { ClientSummary, ClientAggregate, LogEntry, OrgConfig, HmrcResponse, ApiEnvelope } from "../../shared/contract";

// Injected at build time by esbuild `define` (see build.mjs); falls back to prod.
declare const __API_ENDPOINT__: string;

const settings = {
    endpoint: __API_ENDPOINT__
};

/**
 * Typed client for the backend API — the single boundary between the web app
 * and the Lambda. Every command POSTs to one endpoint with a Cognito id token.
 */
export class Api {
    private token: string;

    public setToken(token: string) {
        this.token = token;
    }

    public getToken(): string {
        return this.token;
    }

    /** Swap an HMRC authorization code for an access token (stored server-side). */
    public async authorize(code: string): Promise<void> {
        await this.call("authorize", { code });
    }

    public async getClients(): Promise<ClientSummary[]> {
        const res = await this.call("get-clients", {});
        return res.status?.success ? (res.data as ClientSummary[]) : [];
    }

    public async getClient(investorId: string): Promise<ClientAggregate> {
        const res = await this.call("get-client", { investorId });
        if (!res.status?.success)
            throw new Error("Investor not found");
        return res.data as ClientAggregate;
    }

    public async getLog(): Promise<LogEntry[]> {
        const res = await this.call("get-log", {});
        if (!res.status?.success)
            throw new Error("No logs");
        return res.data as LogEntry[];
    }

    public async getOrgConfig(): Promise<OrgConfig> {
        const res = await this.call("get-org", {});
        if (!res.status?.success)
            throw new Error("Failed to load org config");
        const cfg = res.data as OrgConfig;
        if (!cfg?.env || !cfg?.hmrcClientId)
            throw new Error("Incomplete org config — env or hmrcClientId missing");
        return cfg;
    }

    /**
     * Execute a LISA command. The result shape varies by command/outcome: a raw
     * HMRC response (status + data) for reads/errors, or a refreshed client
     * aggregate after a successful write. Callers narrow by checking `.status`.
     */
    public async lisa(command: string, payload: Payload): Promise<HmrcResponse | ClientAggregate> {
        const res = await this.call(command, payload);
        if (!res.status?.success)
            throw new Error("LISA command failed: " + command);
        return res.data;
    }

    private async call(command: string, data?: Record<string, any>): Promise<ApiEnvelope<any>> {
        try {
            Utils.showLoader();
            const response = await fetch(settings.endpoint, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token}`,
                },
                body: JSON.stringify({ command, data }),
            });
            Utils.hideLoader();

            const json = await response.json() as ApiEnvelope<any>;
            if (response.ok)
                return json;
            console.error(`API error: ${command} → ${response.status}`);
            throw new Error(`HTTP error: ${response.status}`);
        } catch (error) {
            Utils.hideLoader();
            console.error(`API request failed: ${command}`);
            return { status: { success: false }, data: null, error: String(error) };
        }
    }
}
